import { Field, useFormikContext } from 'formik';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { AddressFieldProps } from '../../types/fields';
import { config } from '../../utils/config';
import { Country } from '../../utils/countryData';
import getValidator from '../../utils/getValidator';
import getZipCodeValidator, { ZipCodeValidation } from '../../utils/getZipCodeCityValidator';
import TextField from '../formik/TextField';

type ZipCodeCityMapping = Record<string, string>;
type ZipCodeCityMappings = Partial<Record<Country, ZipCodeCityMapping>>;
const cachedCityZipCodeMappings: ZipCodeCityMappings = {};

type ZipCodeLists = Partial<Record<Country, string[]>>;
const cachedZipCodeLists: ZipCodeLists = {};

const loadZipCodeCityMapping = async (country: Country) => {
  try {
    const zipCodeCityMapping: ZipCodeCityMapping = await (
      await fetch(`https://checkout-cdn.aboutyou.cloud/data/zip-code-city-mappings/${country}.json`)
    ).json();
    cachedCityZipCodeMappings[country] = zipCodeCityMapping;
  } catch (err) {
    console.log(err);
  }
};

export const useCityCompletion = (selectedCountry: Country) => {
  useEffect(() => {
    const isCityCompletionEnabled = config.addressForm[selectedCountry].zipCodeCity.completion?.enabled;
    if (!isCityCompletionEnabled || selectedCountry in cachedCityZipCodeMappings) {
      return;
    }
    loadZipCodeCityMapping(selectedCountry);
  }, [selectedCountry]);

  return (zipCodeValue: string) => cachedCityZipCodeMappings[selectedCountry]?.[zipCodeValue.replaceAll(' ', '')];
};

const loadZipCodeList = async (country: Country, type: 'allow' | 'block') => {
  try {
    const zipCodeList: string[] = await (
      await fetch(`https://checkout-cdn.aboutyou.cloud/data/zip-code-${type}-lists/${config.shop}/${country}.json`)
    ).json();
    cachedZipCodeLists[country] = zipCodeList;
  } catch (err) {
    console.log(err);
  }
};

export const useZipCodeValidation = (selectedCountry: Country): ZipCodeValidation => {
  const { allowList, blockList, ...otherValidation } = config.addressForm[selectedCountry].zipCodeCity.zipCode;
  const listType = allowList ? 'allow' : blockList ? 'block' : undefined;
  const zipCodeList = cachedZipCodeLists[selectedCountry];

  useEffect(() => {
    if (!listType || selectedCountry in cachedZipCodeLists) {
      return;
    }
    loadZipCodeList(selectedCountry, listType);
  }, [selectedCountry]);

  return {
    ...otherValidation,
    allowList: listType === 'allow' ? zipCodeList : undefined,
    blockList: listType === 'block' ? zipCodeList : undefined,
  };
};

const ZipCodeCityField = ({ context, testIdPrefix }: AddressFieldProps) => {
  const { formatMessage } = useIntl();
  const { values, getFieldMeta, setFieldValue } = useFormikContext<any>();
  const { value: zipCodeValue, error: zipCodeError } = getFieldMeta(`${context}.zipCode`);

  const selectedCountry: Country = values[context].country;
  const { zipCode: zipCodeConfig, city: cityConfig } = config.addressForm[selectedCountry].zipCodeCity;

  const getCity = useCityCompletion(selectedCountry);
  const zipCodeValidation = useZipCodeValidation(selectedCountry);

  const onZipCodeBlur = () => {
    if (!(zipCodeValue && !zipCodeError)) {
      return;
    }

    const suggestedCity = getCity(values[context].zipCode);
    if (suggestedCity) {
      setFieldValue(`${context}.city`, suggestedCity);
    }
  };

  const zipCodeField = (zipCodeConfig.enabled === undefined || zipCodeConfig.enabled) && (
    <div className="form__field-container">
      <Field
        onBlur={onZipCodeBlur}
        name={`${context}.zipCode`}
        component={TextField}
        label={formatMessage({ id: 'address.zipCode' })}
        maxLength={zipCodeConfig.maxLength ?? 8}
        validate={getZipCodeValidator({
          required: true,
          minLength: 3,
          format: 'default',
          ...zipCodeValidation,
        })}
        data-test-id={`${testIdPrefix}-zipCode`}
      />
    </div>
  );

  return (
    <>
      {zipCodeConfig.position === 'first' && zipCodeField}
      <div className="form__field-container" style={{ flex: 4 }}>
        <Field
          name={`${context}.city`}
          component={TextField}
          label={formatMessage({ id: 'address.city' })}
          maxLength={cityConfig?.maxLength ?? 50}
          validate={getValidator({ required: true, minLength: 3, format: 'streetOrCity', ...cityConfig })}
          data-test-id={`${testIdPrefix}-city`}
        />
      </div>
      {zipCodeConfig.position === 'second' && zipCodeField}
    </>
  );
};

export default ZipCodeCityField;
